import React from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Chip,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  LinearProgress
} from '@material-ui/core'
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  ErrorOutline
} from '@material-ui/icons'
import ImportTableCard from './ImportTableCard'

const ImportPreviewModal = ({
  open,
  onClose,
  onImported,
  projectId,
  docId,
  parseResult,
  project,
  url
}) => {
  const sheets = (parseResult && Array.isArray(parseResult.sheets)) ? parseResult.sheets : []
  const buildings = (project && Array.isArray(project.buildings)) ? project.buildings : []

  const [activeSheet, setActiveSheet] = React.useState(0)
  const [activeTable, setActiveTable] = React.useState(0)
  const [tableStates, setTableStates] = React.useState({})
  const [importing, setImporting] = React.useState(false)
  const [error, setError] = React.useState('')

  // Page selection state (for classified/large plan sets)
  const isClassifiedOnly = parseResult && parseResult.classificationOnly === true
  const sheetIndex = (parseResult && Array.isArray(parseResult.sheetIndex)) ? parseResult.sheetIndex : []
  const [selectedPages, setSelectedPages] = React.useState(() => {
    // Pre-select pages with schedules
    return new Set(sheetIndex.filter(si => si.hasSchedule).map(si => si.page))
  })
  const [parsing, setParsing] = React.useState(false)
  const [localParseResult, setLocalParseResult] = React.useState(null)

  // Use localParseResult (after page parse) or the prop
  const effectiveParseResult = localParseResult || parseResult
  const effectiveSheets = (effectiveParseResult && Array.isArray(effectiveParseResult.sheets)) ? effectiveParseResult.sheets : sheets

  const defaultBuilding = React.useMemo(() => {
    if (buildings.length === 1) {
      return typeof buildings[0] === 'string' ? buildings[0] : (buildings[0].name || '')
    }
    if (buildings.length === 0) return 'Main'
    return ''
  }, [buildings])

  const getKey = (s, t) => `${s}:${t}`

  const getState = (s, t) => {
    const k = getKey(s, t)
    if (tableStates[k]) return tableStates[k]
    return { building: defaultBuilding, section: '', skipSet: new Set() }
  }

  const togglePage = (pageNum) => {
    setSelectedPages(prev => {
      const next = new Set(prev)
      if (next.has(pageNum)) next.delete(pageNum)
      else next.add(pageNum)
      return next
    })
  }

  const selectByDiscipline = (discipline) => {
    const pages = sheetIndex.filter(si => si.discipline === discipline).map(si => si.page)
    setSelectedPages(prev => {
      const next = new Set(prev)
      pages.forEach(p => next.add(p))
      return next
    })
  }

  const handleParsePages = async () => {
    if (selectedPages.size === 0) return
    setParsing(true)
    setError('')
    try {
      const { data } = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/parse-pages`,
        { pages: Array.from(selectedPages).sort((a, b) => a - b) }
      )
      // data is the updated document with parseResult
      if (data && data.parseResult) {
        setLocalParseResult(data.parseResult)
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Parse failed')
    }
    setParsing(false)
  }

  const updateState = (s, t, next) => {
    setTableStates(prev => ({ ...prev, [getKey(s, t)]: next }))
  }

  React.useEffect(() => {
    if (!open) {
      setActiveSheet(0)
      setActiveTable(0)
      setTableStates({})
      setError('')
    }
  }, [open])

  const handleSheetChange = (_, v) => {
    setActiveSheet(v)
    setActiveTable(0)
  }

  const handleImport = () => {
    const sheet = sheets[activeSheet]
    const table = sheet && sheet.tables && sheet.tables[activeTable]
    if (!table) return

    const state = getState(activeSheet, activeTable)
    if (!state.building) {
      setError('Please select a building before importing.')
      return
    }
    const rows = Array.isArray(table.rows) ? table.rows : []
    const skipRows = Array.from(state.skipSet || [])
    if (rows.length - skipRows.length <= 0) {
      setError('No rows selected to import.')
      return
    }

    setImporting(true)
    setError('')

    const payload = {
      sheetIndex: activeSheet,
      tableIndex: activeTable,
      building: state.building,
      skipRows
    }
    if (state.section && state.section.trim()) {
      payload.section = state.section.trim()
    }

    axios.post(
      `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/import-items`,
      payload
    ).then(res => {
      setImporting(false)
      const count = res.data?.imported || 0
      if (onImported) onImported(count)
    }).catch(err => {
      setImporting(false)
      setError(err.response?.data?.error || err.response?.data?.message || 'Import failed. Please try again.')
    })
  }

  if (!parseResult || sheets.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import Items</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            This document has no parsed tables to import.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  const currentSheet = sheets[activeSheet] || {}
  const tables = Array.isArray(currentSheet.tables) ? currentSheet.tables : []
  const currentTable = tables[activeTable]
  const currentState = currentTable ? getState(activeSheet, activeTable) : null
  const rowsCount = currentTable && Array.isArray(currentTable.rows) ? currentTable.rows.length : 0
  const importCount = currentState ? rowsCount - (currentState.skipSet ? currentState.skipSet.size : 0) : 0
  const canImport = Boolean(currentTable && currentState && currentState.building && importCount > 0)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Import Items from Document</Typography>
          <IconButton size="small" onClick={onClose} disabled={importing}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {/* Plan Set metadata header */}
        {parseResult && parseResult.documentType === 'plan_set' && (
          <div style={{
            padding: '12px 0',
            marginBottom: 16,
            borderBottom: '1px solid #e0e0e0'
          }}>
            {parseResult.titleBlock && (
              <div style={{ marginBottom: 8 }}>
                {parseResult.titleBlock.projectName && (
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {parseResult.titleBlock.projectName}
                  </Typography>
                )}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
                  {parseResult.titleBlock.address && (
                    <Typography variant="caption" color="textSecondary">
                      {parseResult.titleBlock.address}
                    </Typography>
                  )}
                  {parseResult.titleBlock.architect && (
                    <Typography variant="caption" color="textSecondary">
                      Architect: {parseResult.titleBlock.architect}
                    </Typography>
                  )}
                  {parseResult.titleBlock.date && (
                    <Typography variant="caption" color="textSecondary">
                      Date: {parseResult.titleBlock.date}
                    </Typography>
                  )}
                </div>
              </div>
            )}
            {parseResult.sheetIndex && parseResult.sheetIndex.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {parseResult.sheetIndex.map((si, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    label={`${si.sheetNumber || 'P' + si.page} ${si.discipline}`}
                    style={{
                      backgroundColor: si.hasSchedule ? '#e8f5e9' : '#f5f5f5',
                      color: si.hasSchedule ? '#2e7d32' : '#757575',
                      fontSize: 11
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Page Selection for classified (large) plan sets */}
        {isClassifiedOnly && !localParseResult && (
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              Select Pages to Parse ({parseResult.pageCount} total pages)
            </Typography>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={() => selectByDiscipline('landscape')}>
                All Landscape
              </Button>
              <Button size="small" variant="outlined" onClick={() => selectByDiscipline('irrigation')}>
                All Irrigation
              </Button>
              <Button size="small" variant="outlined" onClick={() => selectByDiscipline('tree_inventory')}>
                All Tree Inventory
              </Button>
              <Button size="small" variant="outlined" onClick={() => selectByDiscipline('hardscape')}>
                All Hardscape
              </Button>
              <Button size="small" variant="outlined"
                onClick={() => setSelectedPages(new Set(sheetIndex.filter(si => si.hasSchedule).map(si => si.page)))}>
                All with Schedules
              </Button>
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Page</TableCell>
                    <TableCell>Discipline</TableCell>
                    <TableCell>Sheet #</TableCell>
                    <TableCell>Schedule</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sheetIndex.map(si => {
                    const disciplineColors = {
                      landscape: '#2e7d32', irrigation: '#1565c0', hardscape: '#e65100',
                      tree_inventory: '#6a1b9a', lighting: '#f9a825', demolition: '#c62828',
                      cover: '#546e7a', notes: '#546e7a', detail: '#546e7a', other: '#9e9e9e'
                    }
                    return (
                      <TableRow key={si.page} hover
                        style={{ backgroundColor: selectedPages.has(si.page) ? '#e8f5e9' : undefined }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedPages.has(si.page)}
                            onChange={() => togglePage(si.page)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{si.page}</TableCell>
                        <TableCell>
                          <Chip size="small" label={si.discipline.replace('_', ' ')}
                            style={{
                              backgroundColor: disciplineColors[si.discipline] || '#9e9e9e',
                              color: '#fff', fontSize: 11
                            }} />
                        </TableCell>
                        <TableCell>{si.sheetNumber || '—'}</TableCell>
                        <TableCell>{si.hasSchedule ? 'Yes' : '—'}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            {parsing && (
              <div style={{ marginTop: 16 }}>
                <LinearProgress />
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                  Parsing {selectedPages.size} pages... This may take a few minutes for large plans.
                </Typography>
              </div>
            )}
            {error && (
              <Typography color="error" variant="body2" style={{ marginTop: 8 }}>{error}</Typography>
            )}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleParsePages}
                disabled={selectedPages.size === 0 || parsing}
              >
                {parsing ? 'Parsing...' : `Parse ${selectedPages.size} Selected Pages`}
              </Button>
            </div>
          </div>
        )}

        {/* Normal import flow (after parsing is done) */}
        {(!isClassifiedOnly || localParseResult) && effectiveSheets.length > 1 && (
          <Paper elevation={0} style={{ border: '1px solid #eeeeee', marginBottom: 16 }}>
            <Tabs
              value={activeSheet}
              onChange={handleSheetChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {sheets.map((s, i) => (
                <Tab key={i} label={s.name || `Sheet ${i + 1}`} />
              ))}
            </Tabs>
          </Paper>
        )}

        <Paper elevation={0} style={{ padding: 16, border: '1px solid #eeeeee' }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
            {currentSheet.name || `Sheet ${activeSheet + 1}`}
          </Typography>

          {tables.length > 1 && (
            <Tabs
              value={activeTable}
              onChange={(_, v) => setActiveTable(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              style={{ marginBottom: 16, borderBottom: '1px solid #eeeeee' }}
            >
              {tables.map((_, i) => (
                <Tab key={i} label={`Table ${i + 1}`} />
              ))}
            </Tabs>
          )}

          {currentTable ? (
            <ImportTableCard
              sheetIndex={activeSheet}
              tableIndex={activeTable}
              table={currentTable}
              buildings={buildings}
              state={currentState}
              onChange={updateState}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              No tables found in this sheet.
            </Typography>
          )}
        </Paper>

        {error && (
          <div style={{
            marginTop: 16,
            padding: '12px 16px',
            backgroundColor: '#ffebee',
            borderRadius: 4,
            borderLeft: '4px solid #c62828',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <ErrorOutline style={{ color: '#c62828' }} />
            <Typography variant="body2" color="error">{error}</Typography>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="textSecondary" style={{ marginRight: 'auto', marginLeft: 8 }}>
          {currentTable ? `${importCount} of ${rowsCount} rows selected` : ''}
        </Typography>
        <Button onClick={onClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          color="primary"
          disabled={!canImport || importing}
          startIcon={importing ? <CircularProgress size={18} color="inherit" /> : <GetAppIcon />}
        >
          {importing ? 'Importing…' : 'Import Items'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImportPreviewModal
